import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import {
  CalendarDays,
  Check,
  ChevronLeft,
  ChevronRight,
  Clock3,
  CreditCard,
  Repeat2,
  RotateCcw,
} from 'lucide-react';
import {
  currentMonthSP,
  daysInMonth,
  formatMonthLabel,
  formatYmd,
  parseYm,
  parseYmd,
  todaySP,
  type ExpenseCalendarDay,
  type ExpenseCalendarItem,
  type ExpenseCalendarRecurrence,
  type ExpenseCalendarResponse,
  type ExpenseCalendarStatus,
} from '@lira/shared';
import { apiGet, apiPost } from '@/lib/api';
import { errorMessage } from '@/lib/errorMessage';
import { cn } from '@/lib/cn';
import { readableTextOn } from '@/lib/contrast';
import { useMonthStore } from '@/stores/month';
import { toast } from '@/stores/toast';
import { CategoryIcon, Modal, MoneyText, Skeleton, Toggle } from '@/components/ui';
import { PageHeader } from '@/components/layout/PageHeader';
import { TransactionDetailsModal } from '@/features/transactions/TransactionDetailsModal';
import { InvoiceTransactionsModal } from '@/features/transactions/InvoiceTransactionsModal';

const WEEKDAYS = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'] as const;
const WEEKDAYS_LONG = ['domingo', 'segunda-feira', 'terça-feira', 'quarta-feira', 'quinta-feira', 'sexta-feira', 'sábado'] as const;

const STATUS_OPTIONS: Array<{ value: ExpenseCalendarStatus; label: string }> = [
  { value: 'all', label: 'Todas' },
  { value: 'pending', label: 'Pendentes' },
  { value: 'effected', label: 'Realizadas' },
];

const RECURRENCE_OPTIONS: Array<{ value: ExpenseCalendarRecurrence; label: string }> = [
  { value: 'all', label: 'Qualquer frequência' },
  { value: 'fixed', label: 'Fixas' },
  { value: 'installment', label: 'Parceladas' },
  { value: 'repeat', label: 'Repetidas' },
  { value: 'none', label: 'Avulsas' },
];

const VALID_MONTH = /^\d{4}-(0[1-9]|1[0-2])$/;
const VALID_STATUSES = new Set<ExpenseCalendarStatus>(STATUS_OPTIONS.map((option) => option.value));
const VALID_RECURRENCES = new Set<ExpenseCalendarRecurrence>(RECURRENCE_OPTIONS.map((option) => option.value));

function monthBounds(month: string): { from: string; to: string } {
  const { year, month: m } = parseYm(month);
  return { from: formatYmd(year, m, 1), to: formatYmd(year, m, daysInMonth(year, m)) };
}

function detailedDate(date: string): string {
  const { year, month, day } = parseYmd(date);
  const weekday = WEEKDAYS_LONG[new Date(Date.UTC(year, month - 1, day)).getUTCDay()];
  return `${weekday}, ${String(day).padStart(2, '0')}/${String(month).padStart(2, '0')}/${year}`;
}

function calendarMonthLabel(month: string): string {
  const parsed = parseYm(month);
  return new Intl.DateTimeFormat('pt-BR', { month: 'long', year: 'numeric', timeZone: 'UTC' })
    .format(new Date(Date.UTC(parsed.year, parsed.month - 1, 1)));
}

function buildMonthCells(month: string): Array<string | null> {
  const { year, month: m } = parseYm(month);
  const firstWeekday = new Date(Date.UTC(year, m - 1, 1)).getUTCDay();
  const cells: Array<string | null> = Array.from({ length: firstWeekday }, () => null);
  for (let day = 1; day <= daysInMonth(year, m); day += 1) cells.push(formatYmd(year, m, day));
  while (cells.length % 7 !== 0) cells.push(null);
  return cells;
}

function queryString(
  from: string,
  to: string,
  status: ExpenseCalendarStatus,
  recurrence: ExpenseCalendarRecurrence,
  groupFixedCards: boolean,
): string {
  const params = new URLSearchParams({
    from,
    to,
    type: 'all',
    status,
    recurrence,
    groupFixedCards: groupFixedCards ? 'true' : 'false',
  });
  return params.toString();
}

function SummaryCard({
  label,
  cents,
  count,
  tone,
  icon: Icon,
  onClick,
}: {
  label: string;
  cents: number;
  count: number;
  tone: string;
  icon: typeof Clock3;
  onClick: () => void;
}) {
  return (
    <button type="button" onClick={onClick} className="w-full rounded-[var(--radius-panel)] bg-surface p-4 text-left shadow-card transition-shadow hover:shadow-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary">
      <div className="flex items-start gap-3">
        <span className={cn('grid size-10 shrink-0 place-items-center rounded-full text-white', tone)}>
          <Icon className="size-5" />
        </span>
        <div className="min-w-0">
          <p className="text-xs font-semibold uppercase tracking-wide text-muted">{label}</p>
          <MoneyText cents={cents} bold className="mt-1 block text-lg" />
          <p className="mt-0.5 text-xs text-muted">{count} {count === 1 ? 'despesa' : 'despesas'}</p>
        </div>
      </div>
    </button>
  );
}

function ExpenseRow({
  item,
  onEffectuate,
  onDetails,
  onOpenGroup,
  pending,
}: {
  item: ExpenseCalendarItem;
  onEffectuate: (id: string) => void;
  onDetails: (id: string) => void;
  onOpenGroup: (item: Extract<ExpenseCalendarItem, { kind: 'fixed_card_group' }>) => void;
  pending: boolean;
}) {
  if (item.kind === 'fixed_card_group') {
    return (
      <li className="min-w-0 rounded-xl border border-cards/25 bg-cards/5">
        <button
          type="button"
          aria-label={item.description}
          onClick={() => onOpenGroup(item)}
          className="flex w-full min-w-0 flex-wrap items-center gap-3 px-3 py-3 text-left sm:flex-nowrap"
        >
          <span className="grid size-[38px] shrink-0 place-items-center rounded-full bg-cards text-white"><CreditCard className="size-5" /></span>
          <span className="min-w-0 flex-1">
            <span className="block truncate text-sm font-semibold text-ink">{item.description}</span>
            <span className="mt-1 block text-[11px] text-muted">{item.groupedItems.length} {item.groupedItems.length === 1 ? 'despesa fixa' : 'despesas fixas'} · vencimento</span>
          </span>
          <span
            data-testid="card-group-total-pill"
            className="shrink-0 rounded-full px-3 py-1.5 text-sm font-bold tabular-nums shadow-sm"
            style={{ backgroundColor: item.card.color, color: readableTextOn(item.card.color) }}
          >
            <MoneyText cents={item.amountCents} bold className="text-inherit" />
          </span>
        </button>
      </li>
    );
  }

  const fixed = item.recurrenceMode === 'fixed';
  const income = item.type === 'income';
  const description = item.description || item.category?.name || (income ? 'Receita' : 'Despesa');
  const typeLabel = income ? 'Receita' : 'Despesa';
  const statusLabel = income
    ? (item.effectuated ? 'Recebida' : 'A receber')
    : (item.effectuated ? 'Paga' : 'Pendente');
  return (
    <li className="flex min-w-0 flex-wrap items-center gap-2 rounded-xl border border-border bg-surface px-2 py-2 sm:flex-nowrap">
      <button
        type="button"
        aria-label={`Ver detalhes de ${description}`}
        onClick={() => onDetails(item.id)}
        className="flex min-w-0 flex-1 items-center gap-3 rounded-lg px-1 py-1 text-left hover:bg-canvas"
      >
        <CategoryIcon
          icon={item.category?.icon}
          color={item.category?.color ?? '#6515DD'}
          fallbackText={item.category?.name ?? item.description}
          size={38}
        />
        <span className="min-w-0 flex-1">
          <span className="block truncate text-sm font-semibold text-ink">{description}</span>
          <span className="mt-1 flex flex-wrap items-center gap-1.5 text-[11px] text-muted">
            <span>{item.category?.name ?? 'Sem categoria'}</span>
            <span className={cn('rounded-full px-2 py-0.5 font-semibold', income ? 'bg-income-weak text-income' : 'bg-expense-weak text-expense')}>
              {income ? '↑' : '↓'} {typeLabel}
            </span>
            {fixed && <span className="inline-flex items-center gap-1 rounded-full bg-primary-weak px-2 py-0.5 text-primary"><Repeat2 className="size-3" />Fixa</span>}
            <span className={cn('rounded-full px-2 py-0.5', item.effectuated ? 'bg-income-weak text-income' : 'bg-alert/10 text-alert')}>
              {statusLabel}
            </span>
            {item.installmentIndex && item.installmentTotal && <span>{item.installmentIndex}/{item.installmentTotal}</span>}
            <span>{item.account?.name ?? item.card?.name ?? ''}</span>
          </span>
        </span>
      </button>
      <div className="flex w-full shrink-0 items-center justify-between gap-2 border-t border-border px-1 pt-2 text-right sm:block sm:w-auto sm:border-0 sm:pt-0">
        <MoneyText cents={item.amountCents} colorBySign showPositiveSign={income} bold className="text-sm" />
        {!item.effectuated && !item.card && item.date <= todaySP() && (
          <button
            type="button"
            disabled={pending}
            onClick={() => onEffectuate(item.id)}
            className="mt-1 min-h-8 rounded-full px-2 text-[11px] font-semibold uppercase tracking-wide text-primary hover:bg-primary-weak disabled:opacity-50"
          >
            {income ? 'Receber' : 'Pagar'}
          </button>
        )}
      </div>
    </li>
  );
}

function DayPanel({
  day,
  date,
  onEffectuate,
  onDetails,
  onOpenGroup,
  mutationPending,
}: {
  day: ExpenseCalendarDay | undefined;
  date: string;
  onEffectuate: (id: string) => void;
  onDetails: (id: string) => void;
  onOpenGroup: (item: Extract<ExpenseCalendarItem, { kind: 'fixed_card_group' }>) => void;
  mutationPending: boolean;
}) {
  return (
    <aside data-testid="calendar-day-panel" className="min-w-0 rounded-[var(--radius-card)] bg-surface p-3 shadow-card sm:p-4 lg:sticky lg:top-4 lg:max-h-[calc(100dvh-2rem)] lg:self-start lg:overflow-y-auto">
      <div className="border-b border-border pb-3">
        <p className="text-xs font-semibold uppercase tracking-wide text-muted">Dia selecionado</p>
        <h2 className="mt-1 font-semibold capitalize text-ink">{detailedDate(date)}</h2>
        <div className="mt-3 grid grid-cols-3 gap-2 text-sm">
          <div className="min-w-0"><span className="block text-[11px] text-muted">Receitas</span><MoneyText cents={day?.incomeCents ?? 0} colorBySign showPositiveSign bold className="block truncate text-income" /></div>
          <div className="min-w-0"><span className="block text-[11px] text-muted">Despesas</span><MoneyText cents={-(day?.expenseCents ?? 0)} colorBySign bold className="block truncate text-expense" /></div>
          <div className="min-w-0 text-right"><span className="block text-[11px] text-muted">Balanço</span><MoneyText cents={(day?.incomeCents ?? 0) - (day?.expenseCents ?? 0)} colorBySign showPositiveSign bold className="block truncate" /></div>
        </div>
      </div>
      {!day || day.items.length === 0 ? (
        <div className="grid min-h-44 place-items-center text-center">
          <div>
            <CalendarDays className="mx-auto size-8 text-muted" />
            <p className="mt-2 text-sm text-muted">Nenhum lançamento neste dia.</p>
          </div>
        </div>
      ) : (
        <ul className="mt-3 space-y-2">
          {day.items.map((item) => (
            <ExpenseRow key={item.id} item={item} onEffectuate={onEffectuate} onDetails={onDetails} onOpenGroup={onOpenGroup} pending={mutationPending} />
          ))}
        </ul>
      )}
    </aside>
  );
}

type CalendarListKind = 'pending' | 'effected' | 'fixed';
type FixedCardGroup = Extract<ExpenseCalendarItem, { kind: 'fixed_card_group' }>;

const LIST_TITLES: Record<CalendarListKind, string> = {
  pending: 'Despesas pendentes',
  effected: 'Despesas realizadas',
  fixed: 'Despesas fixas',
};

function CalendarItemsModal({
  kind,
  days,
  from,
  to,
  onClose,
  onEffectuate,
  onDetails,
  onOpenGroup,
  mutationPending,
}: {
  kind: CalendarListKind | null;
  days: ExpenseCalendarDay[];
  from: string;
  to: string;
  onClose: () => void;
  onEffectuate: (id: string) => void;
  onDetails: (id: string) => void;
  onOpenGroup: (item: FixedCardGroup) => void;
  mutationPending: boolean;
}) {
  const filterItem = (item: ExpenseCalendarItem): ExpenseCalendarItem | null => {
    if (item.type !== 'expense') return null;
    const periodItems = item.kind === 'fixed_card_group'
      ? item.groupedItems.filter((transaction) => transaction.date >= from && transaction.date <= to)
      : null;
    if (kind === 'fixed') {
      if (item.kind === 'transaction') return item.recurrenceMode === 'fixed' ? item : null;
      const groupedItems = (periodItems ?? []).filter((transaction) => transaction.recurrenceMode === 'fixed');
      if (groupedItems.length === 0) return null;
      return {
        ...item,
        amountCents: -groupedItems.reduce((sum, transaction) => sum + Math.abs(transaction.amountCents), 0),
        effectuated: groupedItems.every((transaction) => transaction.effectuated),
        pendingCents: groupedItems.filter((transaction) => !transaction.effectuated)
          .reduce((sum, transaction) => sum + Math.abs(transaction.amountCents), 0),
        effectedCents: groupedItems.filter((transaction) => transaction.effectuated)
          .reduce((sum, transaction) => sum + Math.abs(transaction.amountCents), 0),
        groupedItems,
      };
    }
    if (item.kind === 'transaction') {
      return (kind === 'pending' ? !item.effectuated : item.effectuated) ? item : null;
    }
    const groupedItems = (periodItems ?? []).filter((transaction) => (
      kind === 'pending' ? !transaction.effectuated : transaction.effectuated
    ));
    if (groupedItems.length === 0) return null;
    const pendingCents = groupedItems.filter((transaction) => !transaction.effectuated)
      .reduce((sum, transaction) => sum + Math.abs(transaction.amountCents), 0);
    const effectedCents = groupedItems.filter((transaction) => transaction.effectuated)
      .reduce((sum, transaction) => sum + Math.abs(transaction.amountCents), 0);
    return {
      ...item,
      amountCents: -groupedItems.reduce((sum, transaction) => sum + Math.abs(transaction.amountCents), 0),
      effectuated: groupedItems.every((transaction) => transaction.effectuated),
      pendingCents,
      effectedCents,
      groupedItems,
    };
  };
  const grouped = days
    .map((day) => ({
      date: day.date,
      items: day.items.map(filterItem).filter((item): item is ExpenseCalendarItem => item !== null),
    }))
    .filter((day) => day.items.length > 0)
    .sort((a, b) => a.date.localeCompare(b.date));

  return (
    <Modal open={!!kind} onClose={onClose} title={kind ? LIST_TITLES[kind] : undefined} maxWidth={680}>
      {grouped.length === 0 ? (
        <p className="rounded-lg border border-border px-4 py-8 text-center text-sm text-muted">Nenhuma despesa nesta lista.</p>
      ) : (
        <div className="min-w-0 space-y-4">
          {grouped.map((day) => (
            <section key={day.date} className="min-w-0">
              <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted">{detailedDate(day.date)}</h3>
              <ul className="min-w-0 space-y-2">
                {day.items.map((item) => (
                  <ExpenseRow key={item.id} item={item} onEffectuate={onEffectuate} onDetails={onDetails} onOpenGroup={onOpenGroup} pending={mutationPending} />
                ))}
              </ul>
            </section>
          ))}
        </div>
      )}
    </Modal>
  );
}

function FixedCardGroupModal({
  group,
  onClose,
  onDetails,
  onPayInvoice,
}: {
  group: FixedCardGroup | null;
  onClose: () => void;
  onDetails: (id: string) => void;
  onPayInvoice: (group: FixedCardGroup) => void;
}) {
  return (
    <Modal open={!!group} onClose={onClose} title={group?.description} maxWidth={620}>
      {group && (
        <div className="min-w-0 space-y-3">
          <div className="flex min-w-0 items-center justify-between gap-3 rounded-xl bg-cards/10 p-3">
            <span className="min-w-0 text-sm text-muted">Fechamento {detailedDate(group.date)} • vence {detailedDate(group.dueDate)}</span>
            <MoneyText cents={group.amountCents} colorBySign bold className="shrink-0" />
          </div>
          <button
            type="button"
            onClick={() => onPayInvoice(group)}
            className="inline-flex min-h-11 items-center gap-2 rounded-full bg-cards px-5 text-sm font-semibold uppercase tracking-wide text-white hover:opacity-90"
          >
            <CreditCard className="size-4" />Pagar fatura
          </button>
          <ul className="overflow-hidden rounded-xl border border-border">
            {group.groupedItems.map((item) => {
              const description = item.description || item.category?.name || 'Despesa fixa';
              return (
                <li key={item.id} className="border-b border-border last:border-0">
                  <button type="button" aria-label={`Ver detalhes de ${description}`} onClick={() => onDetails(item.id)} className="flex w-full min-w-0 items-center gap-3 px-3 py-3 text-left hover:bg-canvas">
                    <CategoryIcon icon={item.category?.icon} color={item.category?.color ?? '#6515DD'} size={34} />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-semibold text-ink">{description}</span>
                      <span className="block truncate text-xs text-muted">{item.category?.name ?? 'Sem categoria'}</span>
                    </span>
                    <MoneyText cents={item.amountCents} colorBySign bold className="shrink-0 text-sm" />
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </Modal>
  );
}

export function ExpenseCalendarPage() {
  const month = useMonthStore((state) => state.month);
  const setMonth = useMonthStore((state) => state.setMonth);
  const stepMonth = useMonthStore((state) => state.step);

  const [params, setParams] = useSearchParams();
  const rawStatus = params.get('status') as ExpenseCalendarStatus | null;
  const rawRecurrence = params.get('recurrence') as ExpenseCalendarRecurrence | null;
  const status: ExpenseCalendarStatus = rawStatus && VALID_STATUSES.has(rawStatus) ? rawStatus : 'all';
  const recurrence: ExpenseCalendarRecurrence = rawRecurrence && VALID_RECURRENCES.has(rawRecurrence)
    ? rawRecurrence
    : 'all';
  const groupFixedCards = params.get('groupFixedCards') === '1';
  const { from, to } = monthBounds(month);
  const today = todaySP();
  const initialSelected = month === currentMonthSP() ? today : from;
  const [selectedDate, setSelectedDate] = useState(initialSelected);
  const [listOpen, setListOpen] = useState<CalendarListKind | null>(null);
  const [detailsId, setDetailsId] = useState<string | null>(null);
  const [fixedCardGroup, setFixedCardGroup] = useState<FixedCardGroup | null>(null);
  const [invoiceToPay, setInvoiceToPay] = useState<FixedCardGroup | null>(null);
  const monthSync = useRef<{ initialized: boolean; urlMonth: string | null; storeMonth: string }>({
    initialized: false,
    urlMonth: null,
    storeMonth: month,
  });
  const queryClient = useQueryClient();

  useEffect(() => {
    const requestedMonth = params.get('month');
    const validUrlMonth = requestedMonth && VALID_MONTH.test(requestedMonth) ? requestedMonth : null;
    const previous = monthSync.current;

    if (!previous.initialized) {
      monthSync.current = { initialized: true, urlMonth: validUrlMonth, storeMonth: month };
      if (validUrlMonth && validUrlMonth !== month) {
        setMonth(validUrlMonth);
        return;
      }
    } else {
      const urlChanged = validUrlMonth !== previous.urlMonth;
      const storeChanged = month !== previous.storeMonth;
      monthSync.current = { initialized: true, urlMonth: validUrlMonth, storeMonth: month };

      if (urlChanged && validUrlMonth && validUrlMonth !== month) {
        setMonth(validUrlMonth);
        return;
      }
      if (storeChanged && validUrlMonth !== month) {
        const next = new URLSearchParams(params);
        next.set('month', month);
        next.set('status', status);
        next.set('recurrence', recurrence);
        setParams(next, { replace: true });
        return;
      }
    }

    setSelectedDate(month === currentMonthSP() ? todaySP() : monthBounds(month).from);
    const next = new URLSearchParams(params);
    next.set('month', month);
    next.set('status', status);
    next.set('recurrence', recurrence);
    if (next.toString() !== params.toString()) setParams(next, { replace: true });
  }, [month, params, recurrence, setMonth, setParams, status]);

  const query = useQuery({
    queryKey: ['expense-calendar', from, to, status, recurrence, groupFixedCards],
    queryFn: () => apiGet<ExpenseCalendarResponse>(`/expense-calendar?${queryString(from, to, status, recurrence, groupFixedCards)}`),
  });

  const effectuate = useMutation({
    mutationFn: (id: string) => apiPost(`/transactions/${id}/effectuate`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['expense-calendar'] });
      void queryClient.invalidateQueries({ queryKey: ['transactions'] });
      void queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      void queryClient.invalidateQueries({ queryKey: ['accounts'] });
      toast('Despesa realizada.', 'success');
    },
    onError: (error) => toast(errorMessage(error), 'error'),
  });

  const days = query.data?.days ?? [];
  const dayByDate = useMemo(() => new Map(days.map((day) => [day.date, day])), [days]);
  const selectedDay = dayByDate.get(selectedDate);
  const cells = useMemo(() => buildMonthCells(month), [month]);

  const updateFilter = (key: 'status' | 'recurrence', value: string) => {
    const next = new URLSearchParams(params);
    next.set(key, value);
    next.set('month', month);
    setParams(next, { replace: true });
  };

  const updateGroupFixedCards = (checked: boolean) => {
    const next = new URLSearchParams(params);
    if (checked) next.set('groupFixedCards', '1');
    else next.delete('groupFixedCards');
    next.set('month', month);
    next.set('status', status);
    next.set('recurrence', recurrence);
    setParams(next, { replace: true });
  };

  return (
    <section className="space-y-5">
      <PageHeader
        title="Calendário"
        subtitle="Visualize receitas, despesas fixas, pendentes e realizadas em qualquer mês."
        actions={(
          <div data-testid="calendar-month-selector" className="flex max-w-full items-center justify-end gap-1 rounded-full border border-border bg-surface p-1 sm:w-auto">
            <button type="button" onClick={() => stepMonth(-1)} aria-label="Mês anterior do calendário" className="grid size-9 place-items-center rounded-full text-muted hover:bg-canvas hover:text-ink"><ChevronLeft className="size-5" /></button>
            <div aria-label={`Mês selecionado: ${calendarMonthLabel(month)}`} className="min-w-0 max-w-40 truncate px-2 py-2 text-xs font-semibold capitalize text-ink sm:max-w-none sm:px-3">{calendarMonthLabel(month)}</div>
            <button type="button" onClick={() => stepMonth(1)} aria-label="Próximo mês do calendário" className="grid size-9 place-items-center rounded-full text-muted hover:bg-canvas hover:text-ink"><ChevronRight className="size-5" /></button>
          </div>
        )}
      />

      <div className="flex flex-col gap-3 rounded-[var(--radius-panel)] bg-surface p-3 shadow-card sm:flex-row sm:flex-wrap sm:items-center sm:justify-between">
        <div className="flex flex-wrap gap-2" aria-label="Status dos lançamentos">
          {STATUS_OPTIONS.map((option) => (
            <button
              key={option.value}
              type="button"
              aria-pressed={status === option.value}
              onClick={() => updateFilter('status', option.value)}
              className={cn('min-h-9 rounded-full border px-3 text-xs font-semibold transition-colors', status === option.value ? 'border-primary bg-primary-weak text-primary' : 'border-border text-muted hover:text-ink')}
            >
              {option.label}
            </button>
          ))}
        </div>
        <label className="flex min-w-0 items-center gap-2 text-xs text-muted">
          Frequência
          <select
            aria-label="Frequência das despesas"
            value={recurrence}
            onChange={(event) => updateFilter('recurrence', event.target.value)}
            className="h-9 min-w-0 flex-1 rounded-full border border-border bg-surface px-3 text-sm text-ink focus:border-primary focus:outline-none sm:flex-none"
          >
            {RECURRENCE_OPTIONS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
          </select>
        </label>
        <Toggle checked={groupFixedCards} onChange={updateGroupFixedCards} accent="cards" label="Agrupar gastos do cartão" className="min-h-9" />
      </div>

      {query.isLoading && <div className="grid gap-3 sm:grid-cols-3"><Skeleton variant="card" className="h-28" /><Skeleton variant="card" className="h-28" /><Skeleton variant="card" className="h-28" /></div>}
      {query.isError && <p className="rounded-lg bg-expense-weak px-4 py-6 text-center text-sm text-expense">{errorMessage(query.error)}</p>}

      {query.data && (
        <>
          <div data-testid="calendar-summary" className="grid gap-3 sm:grid-cols-3">
            <SummaryCard label="Pendentes" cents={query.data.summary.pendingCents} count={query.data.summary.pendingCount} tone="bg-alert" icon={Clock3} onClick={() => setListOpen('pending')} />
            <SummaryCard label="Realizadas" cents={query.data.summary.effectedCents} count={query.data.summary.effectedCount} tone="bg-income" icon={Check} onClick={() => setListOpen('effected')} />
            <SummaryCard label="Fixas no período" cents={query.data.summary.fixedCents} count={query.data.summary.fixedCount} tone="bg-primary" icon={Repeat2} onClick={() => setListOpen('fixed')} />
          </div>

          <div className="grid min-w-0 grid-cols-[minmax(0,1fr)] gap-4 lg:grid-cols-[minmax(0,1.6fr)_minmax(19rem,0.8fr)]">
            <div data-testid="expense-calendar-grid" className="min-w-0 self-start rounded-[var(--radius-card)] bg-surface p-3 shadow-card sm:p-5">
              <div className="mb-4 flex items-center justify-between gap-3">
                <h2 className="text-lg font-semibold text-ink">{formatMonthLabel(month)}</h2>
                {(status !== 'all' || recurrence !== 'all') && (
                  <button type="button" onClick={() => { const next = new URLSearchParams({ month, status: 'all', recurrence: 'all' }); if (groupFixedCards) next.set('groupFixedCards', '1'); setParams(next, { replace: true }); }} className="inline-flex min-h-9 items-center gap-1.5 rounded-full px-3 text-xs font-semibold text-primary hover:bg-primary-weak"><RotateCcw className="size-3.5" />Limpar filtros</button>
                )}
              </div>
              <div className="grid grid-cols-7 border-b border-border pb-2">
                {WEEKDAYS.map((weekday) => <div key={weekday} className="text-center text-[11px] font-semibold uppercase tracking-wide text-muted">{weekday}</div>)}
              </div>
              <div className="grid grid-cols-7">
                {cells.map((date, index) => {
                  if (!date) return <div key={`empty-${index}`} className="min-h-20 border-b border-border/60 sm:min-h-28" aria-hidden />;
                  const day = dayByDate.get(date);
                  const cardGroup = day?.items.find((item): item is FixedCardGroup => item.kind === 'fixed_card_group');
                  const { day: number } = parseYmd(date);
                  const selected = date === selectedDate;
                  const isToday = date === today;
                  return (
                    <button
                      key={date}
                      type="button"
                      onClick={() => setSelectedDate(date)}
                      aria-label={`${detailedDate(date)}${day ? `, ${day.items.length} lançamentos` : ', sem lançamentos'}${cardGroup ? `, ${cardGroup.description}` : ''}`}
                      aria-pressed={selected}
                      className={cn('relative min-h-20 min-w-0 border-b border-border/60 p-1 text-left transition-colors hover:bg-canvas sm:min-h-28 sm:p-2', selected && 'bg-primary-weak/70 ring-2 ring-inset ring-primary')}
                    >
                      <span className={cn('grid size-7 place-items-center rounded-full text-xs font-semibold', isToday ? 'bg-primary text-white' : 'text-ink')}>{number}</span>
                      {day && (
                        <div className="mt-1 min-w-0">
                          {day.incomeCents > 0 && <MoneyText cents={day.incomeCents} colorBySign showPositiveSign bold className="block truncate text-[9px] text-income sm:text-[11px]" />}
                          {day.expenseCents > 0 && <MoneyText cents={-day.expenseCents} colorBySign bold className="block truncate text-[9px] text-expense sm:text-[11px]" />}
                          <div className="mt-1 flex items-center gap-1" aria-hidden>
                            {day.pendingCents > 0 && <span className="size-1.5 rounded-full bg-alert" />}
                            {day.effectedCents > 0 && <span className="size-1.5 rounded-full bg-income" />}
                            {day.fixedCents > 0 && <Repeat2 className="size-3 text-primary" />}
                          </div>
                          <span className="mt-1 hidden truncate text-[10px] text-muted sm:block">{day.items.length} {day.items.length === 1 ? 'despesa' : 'despesas'}</span>
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
              <div className="mt-3 flex flex-wrap items-center gap-4 text-[11px] text-muted">
                <span className="inline-flex items-center gap-1.5"><span className="size-2 rounded-full bg-alert" />Pendente</span>
                <span className="inline-flex items-center gap-1.5"><span className="size-2 rounded-full bg-income" />Realizada</span>
                <span className="inline-flex items-center gap-1.5"><Repeat2 className="size-3 text-primary" />Fixa</span>
              </div>
            </div>

            <DayPanel day={selectedDay} date={selectedDate} onEffectuate={(id) => effectuate.mutate(id)} onDetails={setDetailsId} onOpenGroup={setFixedCardGroup} mutationPending={effectuate.isPending} />
          </div>
        </>
      )}

      <CalendarItemsModal
        kind={listOpen}
        days={days}
        from={from}
        to={to}
        onClose={() => setListOpen(null)}
        onEffectuate={(id) => effectuate.mutate(id)}
        onDetails={setDetailsId}
        onOpenGroup={setFixedCardGroup}
        mutationPending={effectuate.isPending}
      />
      <FixedCardGroupModal
        group={fixedCardGroup}
        onClose={() => setFixedCardGroup(null)}
        onDetails={setDetailsId}
        onPayInvoice={(group) => {
          setFixedCardGroup(null);
          setInvoiceToPay(group);
        }}
      />
      <InvoiceTransactionsModal
        open={!!invoiceToPay}
        card={invoiceToPay ? { id: invoiceToPay.card.id, name: invoiceToPay.card.name, linkedAccountId: null } : null}
        month={invoiceToPay?.invoiceMonth ?? month}
        startPaying
        onClose={() => setInvoiceToPay(null)}
      />
      <TransactionDetailsModal open={!!detailsId} transactionId={detailsId} onClose={() => setDetailsId(null)} />
    </section>
  );
}
